import { type AsyncZipOptions, zip } from 'fflate'
import { match, P } from 'ts-pattern'
import invariant from './invariant'

type SupportedData = Blob | ArrayBuffer | Uint8Array<ArrayBuffer>

export class Zip {
  private files: [string, SupportedData][] = []

  private async prepareFile(
    data: SupportedData,
  ): Promise<Uint8Array<ArrayBuffer>> {
    return await match(data)
      .with(P.instanceOf(Uint8Array), data => data)
      .with(P.instanceOf(ArrayBuffer), data => new Uint8Array(data))
      .with(P.instanceOf(Blob), data =>
        data.arrayBuffer().then(buffer => new Uint8Array(buffer)),
      )
      .exhaustive()
  }

  file(filename: string, data: SupportedData) {
    this.files.push([filename, data])
    return this
  }

  async generateAsync(opts?: AsyncZipOptions) {
    const files: Record<string, Uint8Array<ArrayBuffer>> = {}

    await Promise.all(
      this.files.map(async ([filename, data]) => {
        const prepared = await this.prepareFile(data)
        files[filename] = prepared
      }),
    )

    const zipped = await new Promise<Uint8Array>((resolve, reject) => {
      zip(files, opts ?? {}, (err, data) => {
        if (err) reject(err)
        else resolve(data)
      })
    })
    const { buffer } = zipped
    invariant(buffer instanceof ArrayBuffer, 'Invalid zipped data')

    return new Blob([buffer])
  }
}
